import { useState } from 'react';
import { T, useTheme, Ico, Card, Btn, Field, Input, Textarea, Counter, Toggle } from '../../../design';
import type { ToastData } from '../../../design';
import type { ClaimDraft, DraftItem, OrderLookupResult, Product } from '../../../types/claims.types';
import { emptyDraftItem } from '../../../types/claims.types';
import type { ItemFieldErrors } from '../../../utils/claimErrors';
import { CLAIM_TYPES, typeGroup } from '../claimsConfig';
import { ProductSearch } from './ProductSearch';
import { ImagePicker } from './ImagePicker';

export interface ClaimItemEditorProps {
  initial: DraftItem | null;
  draft: ClaimDraft;
  serverErrors: ItemFieldErrors;
  search: (query: string) => Promise<Product[]>;
  lookupOrder: ((orderNumber: string) => Promise<OrderLookupResult>) | null;
  searchCustomer: ((phone: string) => Promise<{ name: string; phone: string } | null>) | null;
  onSave: (item: DraftItem) => void;
  onCancel: () => void;
  toast: (toast: ToastData) => void;
}

export function ClaimItemEditor({
  initial, draft, serverErrors, search, lookupOrder, searchCustomer, onSave, onCancel, toast,
}: ClaimItemEditorProps) {
  const t = useTheme();
  const [it, setIt] = useState<DraftItem>(initial ?? emptyDraftItem());
  const [order, setOrder] = useState<OrderLookupResult | null>(null);
  const [custSearched, setCustSearched] = useState<boolean>(!!(initial && initial.b2cCustomerName));
  const [touched, setTouched] = useState(false);
  const set = <K extends keyof DraftItem>(k: K, v: DraftItem[K]) => setIt((s) => ({ ...s, [k]: v }));

  const group = it.product ? typeGroup(it.product.category) : null;
  const isWrong = it.claimType === 'wrong_delivery';

  // Én kunde pr. reklamation: andet B2C-nummer på en anden linje = konflikt.
  const existingCust = draft.items.find((x) => x.isB2c && x.b2cMobileNumber && x !== initial);
  const custConflict = !!(it.isB2c && existingCust && it.b2cMobileNumber && existingCust.b2cMobileNumber !== it.b2cMobileNumber);

  const clientErrs: ItemFieldErrors = {};
  if (touched) {
    if (!it.product) clientErrs.product = 'Vælg et produkt.';
    if (!it.claimType) clientErrs.claimType = 'Vælg en reklamationstype.';
    if (isWrong && !it.orderNumber.trim()) clientErrs.orderNumber = 'Ordrenummer er påkrævet ved forkert levering.';
    if (it.isB2c && !it.b2cMobileNumber.trim()) clientErrs.b2cMobileNumber = 'Mobilnummer er påkrævet for kundereklamation.';
  }
  const errs: ItemFieldErrors = { ...serverErrors, ...clientErrs };

  const valid = !!it.product && !!it.claimType
    && !(isWrong && !it.orderNumber.trim())
    && !(it.isB2c && !it.b2cMobileNumber.trim())
    && !custConflict;

  async function checkOrder() {
    if (!lookupOrder) return;
    setOrder(await lookupOrder(it.orderNumber));
  }
  async function doSearchCust() {
    if (!searchCustomer) return;
    const res = await searchCustomer(it.b2cMobileNumber);
    setCustSearched(true);
    if (res) { set('b2cCustomerName', res.name); toast({ tone: 'success', msg: `Kunde fundet: ${res.name}` }); }
    else toast({ tone: 'info', msg: 'Ingen kunde fundet — udfyld navn manuelt.' });
  }
  function save() {
    setTouched(true);
    if (!valid) { toast({ tone: 'error', msg: 'Udfyld de markerede felter.' }); return; }
    onSave({ ...it });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: 96 }}>
      <Field label="Produkt" required error={errs.product}>
        <ProductSearch
          value={it.product}
          search={search}
          onSelect={(p) => { setIt((s) => ({ ...s, product: p, claimType: (p && group !== typeGroup(p.category)) ? null : s.claimType })); }}
        />
      </Field>

      <Field label="Hvad er der galt?" required error={errs.claimType} hint={group === 'elektronik' ? 'Elektronik' : it.product ? 'Standard' : undefined}>
        {!it.product ? (
          <div style={{ fontSize: 13, color: T.fg3, padding: '12px 14px', border: `1.5px dashed ${T.border}`, borderRadius: t.radius, fontWeight: 600 }}>Vælg først et produkt — typerne tilpasses produktets kategori.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {CLAIM_TYPES[group!].map((ct) => {
              const on = it.claimType === ct.value;
              return <button key={ct.value} type="button" onClick={() => set('claimType', ct.value)} style={{ padding: '11px 12px', borderRadius: t.radius, border: `1.5px solid ${on ? t.accent : T.border}`, background: on ? T.accentLight : T.surface, color: on ? t.accent : T.fg2, fontSize: 13, fontWeight: 700, cursor: 'pointer', textAlign: 'left', fontFamily: T.font }}>{ct.label}</button>;
            })}
          </div>
        )}
      </Field>

      {isWrong && (
        <Field label="Ordrenummer" required error={errs.orderNumber} hint="Til forkert levering">
          <div style={{ display: 'flex', gap: 8 }}>
            <Input value={it.orderNumber} onChange={(e) => { set('orderNumber', e.target.value); setOrder(null); }} placeholder="fx 100456" inputMode="numeric" icon={<Ico.receipt size={18} />} style={{ flex: 1 }} />
            {lookupOrder
              ? <Btn variant="secondary" size="md" onClick={checkOrder} disabled={!it.orderNumber.trim()}>Tjek</Btn>
              : null}
          </div>
          {!lookupOrder && <div style={{ marginTop: 8, fontSize: 11.5, color: T.fg3, fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center' }}><Ico.info size={14} />Ordre-opslag er ikke tilgængeligt i admin-mode endnu.</div>}
          {order && (order.matched
            ? <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: t.accent, display: 'flex', alignItems: 'center', gap: 6 }}><Ico.check size={15} />Match i {order.source === 'webshop' ? 'webshop' : 'PARK-ordrer'}</div>
            : <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: T.fg3, display: 'flex', alignItems: 'center', gap: 6 }}><Ico.info size={15} />Intet match — du kan stadig fortsætte.</div>)}
        </Field>
      )}

      <Field label="Antal" required><Counter value={it.quantity} onChange={(v) => set('quantity', v)} /></Field>

      <Field label="Beskrivelse" hint="Valgfri"><Textarea value={it.description} onChange={(e) => set('description', e.target.value)} rows={3} placeholder="Beskriv fejlen…" /></Field>

      <Card pad={14} style={{ background: T.bg }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.fg }}>Kundereklamation (B2C)</div>
            <div style={{ fontSize: 11.5, color: T.fg3, fontWeight: 600, marginTop: 2 }}>Slutkunde reklamerer via salonen</div>
          </div>
          <Toggle value={it.isB2c} onChange={(v) => set('isB2c', v)} />
        </div>
        {it.isB2c && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field label="Kundens mobilnummer" required error={errs.b2cMobileNumber}>
              <div style={{ display: 'flex', gap: 8 }}>
                <Input value={it.b2cMobileNumber} onChange={(e) => { set('b2cMobileNumber', e.target.value); setCustSearched(false); }} placeholder="8 cifre" inputMode="tel" icon={<Ico.phone size={17} />} style={{ flex: 1 }} />
                {searchCustomer
                  ? <Btn variant="secondary" size="md" onClick={doSearchCust} disabled={(it.b2cMobileNumber || '').replace(/\D/g, '').length < 8}>Søg</Btn>
                  : null}
              </div>
            </Field>
            {!searchCustomer && <div style={{ fontSize: 11.5, color: T.fg3, fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center' }}><Ico.info size={14} />Kunde-opslag er ikke tilgængeligt i admin-mode endnu — udfyld navn manuelt.</div>}
            {custConflict && <div style={{ fontSize: 12, fontWeight: 600, color: T.error, display: 'flex', gap: 6, alignItems: 'flex-start' }}><Ico.alert size={15} style={{ flexShrink: 0, marginTop: 1 }} />En reklamation kan kun vedrøre én kunde. Et andet nummer er allerede brugt på en varelinje.</div>}
            {(custSearched || !searchCustomer) && !custConflict && <Field label="Kundenavn"><Input value={it.b2cCustomerName} onChange={(e) => set('b2cCustomerName', e.target.value)} placeholder="Kundens navn" /></Field>}
          </div>
        )}
      </Card>

      <Field label="Billeder" hint="0–5 pr. vare">
        <ImagePicker files={it.media} onChange={(files) => set('media', files)} onReject={(msg) => toast({ tone: 'error', msg })} />
      </Field>

      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30, background: T.surface, borderTop: `1px solid ${T.border}`, padding: '12px 14px calc(12px + env(safe-area-inset-bottom))', display: 'flex', gap: 10, maxWidth: 440, margin: '0 auto' }}>
        <Btn variant="secondary" size="lg" onClick={onCancel} style={{ flex: '0 0 auto' }}>Annullér</Btn>
        <Btn size="lg" full onClick={save} icon={<Ico.check size={19} />}>{initial ? 'Gem vare' : 'Tilføj vare'}</Btn>
      </div>
    </div>
  );
}
